import { api } from '@organization/api'
import { AuthWrapper } from '@organization/features/authentication/components/AuthWrapper'
import { OrganizationLogo } from '@organization/features/authentication/components/OrganizationLogo'
import { useSuccessLogin } from '@organization/hooks'
import { paths } from '@organization/router'
import { deviceSession, showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Button, Card, Flex, Form, Input } from 'antd'
import { Lock1 } from 'iconsax-react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { useStyle } from '../components/AuthWrapper/styles'

export const LoginForm = () => {
  const [form] = Form.useForm()
  const { auth } = useSuccessLogin()
  const { notification } = App.useApp()

  const { t } = useTranslation()
  const { styles } = useStyle()
  const navigate = useNavigate()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const { deviceToken } = deviceSession()

  const { mutate: sendOtp, isPending: isPendingSendOtp } = useMutation({
    mutationFn: api.mfa.sendOtp,

    onSuccess: (data, variables) => {
      const pathname = paths.auth.otpConfirm

      navigate(
        { pathname },
        {
          relative: 'path',
          state: { ...data, ...variables, __created_at: new Date() }
        }
      )
    },

    onError: (error) => {
      showApiErrors(error, form)
    }
  })

  const { mutate: loginUser, isPending: isPendingLoginUser } = useMutation({
    mutationFn: api.auth.login,

    onSuccess: async (token) => {
      if (token.mfa_required && token.mfa_access_token) {
        sendOtp({ __mfaAccessToken: token.mfa_access_token })

        return
      }

      await auth(token)
    },

    onError: (error) => {
      showApiErrors(error, form)
    }
  })

  const onSubmit = async () => {
    if (!executeRecaptcha) {
      notification.error({
        message: t('error.recaptcha_unavailable'),
        description: t('error.recaptcha_unavailable_description')
      })
      return
    }

    const data = form.getFieldsValue()

    try {
      const recaptcha_token = await executeRecaptcha('login')

      loginUser({
        ...data,
        username: data.email,
        recaptcha_token,
        device_token: deviceToken
      })
    } catch {
      notification.error({
        message: t('error.recaptcha_failed'),
        description: t('error.recaptcha_failed_description')
      })
    }
  }

  return (
    <AuthWrapper>
      <Card className={styles.card}>
        <Flex vertical gap={16}>
          <OrganizationLogo
            message={(name) =>
              `${t('auth.sign_in_header')} ${name} ${t('common.account')}`
            }
          />
        </Flex>

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onSubmit}
          className={styles.form}
        >
          <Form.Item
            label={t('form.item.email')}
            name="email"
            rules={[
              { required: true, message: t('form.message.email_required') }
            ]}
          >
            <Input placeholder={t('form.placeholder.email')} />
          </Form.Item>

          <Form.Item
            label={t('form.item.password')}
            name="password"
            rules={[{ required: true, message: t('form.message.password') }]}
          >
            <Input.Password placeholder={t('form.message.password')} />
          </Form.Item>

          <Flex
            justify="space-between"
            align="center"
            style={{ marginBottom: 16 }}
          >
            <Link to={'/authentication/reset-password'}>
              <Flex align="center" gap={8}>
                <Lock1 size={16} /> {t('button.forgot_password')}?
              </Flex>
            </Link>
          </Flex>

          <Flex justify="center" align="center" vertical gap={16}>
            <Button
              block
              type="primary"
              loading={isPendingLoginUser || isPendingSendOtp}
              disabled={!executeRecaptcha}
              htmlType="submit"
            >
              {t('button.sign_in')}
            </Button>
          </Flex>
        </Form>
      </Card>
    </AuthWrapper>
  )
}
